/**
 * Enhanced Evidence Library Page
 * Complete Evidence Hub module with:
 * - Stats dashboard
 * - Search & advanced filters
 * - Grid/List views
 * - Bulk selection & actions
 * - Hybrid detail drawer
 * 
 * Connected to backend API: GET /api/v1/evidence
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { 
  FolderOpen, 
  Search, 
  Filter, 
  Grid, 
  List,
  FileText,
  FileSpreadsheet,
  FileImage,
  File,
  Download,
  Eye,
  Tag,
  Calendar,
  CheckCircle2,
  Clock,
  Plus,
  RefreshCw,
  AlertTriangle,
  ExternalLink,
  Upload,
  Trash2,
  Users,
  Link as LinkIcon,
  Square,
  CheckSquare,
  X,
  Network,
  Link2,
  Sparkles
} from 'lucide-react';
import { API } from '../../App';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import { useToast } from '../../hooks/use-toast';
import { EvidenceDetailDrawer } from '../../components/evidence/EvidenceDetailDrawer';
import LineageDrawer, { useLineageDrawer } from '../../components/audit/LineageDrawer';
import SimilarEvidenceModal from '../../components/SimilarEvidenceModal';
import { JourneySection, JourneyPanel } from '../journeys/JourneyPanel';
import { CrossFrameworkMap, EFSearchPanel, PIIScanner, ConnectorConfig } from '../journeys/JourneyEvidence';
import { apiFetch } from '../../services/api_service';
import {
  extractEvidenceRows,
  extractListTotal,
  normalizeEvidence,
  hasActiveFilter,
  evidenceViewState,
} from './evidenceData';


// ============================================================================
// CONSTANTS & HELPERS
// ============================================================================

const TYPE_ICONS = {
  pdf: FileText,
  xlsx: FileSpreadsheet,
  xls: FileSpreadsheet,
  csv: FileSpreadsheet,
  doc: FileText,
  docx: FileText,
  png: FileImage,
  jpg: FileImage,
  jpeg: FileImage,
  default: File,
};

const STATUS_CONFIG = {
  draft: { label: 'Draft', bg: 'bg-[#1F1F1F]', text: 'text-[#6B6B6B]', icon: Clock },
  pending_review: { label: 'Pending Review', bg: 'bg-amber-500/20', text: 'text-amber-400', icon: Clock },
  pending: { label: 'Pending', bg: 'bg-amber-500/20', text: 'text-amber-400', icon: Clock },
  approved: { label: 'Approved', bg: 'bg-emerald-500/20', text: 'text-emerald-400', icon: CheckCircle2 },
  rejected: { label: 'Rejected', bg: 'bg-red-500/20', text: 'text-red-400', icon: AlertTriangle },
  verified: { label: 'Verified', bg: 'bg-emerald-500/20', text: 'text-emerald-400', icon: CheckCircle2 },
  uploaded: { label: 'Uploaded', bg: 'bg-blue-500/20', text: 'text-blue-400', icon: Upload },
  // Ingestion/parse states from the document pipeline — render them truthfully
  // rather than mislabelling an unparsed upload as "Uploaded".
  processing: { label: 'Processing', bg: 'bg-blue-500/20', text: 'text-blue-400', icon: Clock },
  parsed: { label: 'Parsed', bg: 'bg-emerald-500/20', text: 'text-emerald-400', icon: CheckCircle2 },
  parse_failed: { label: 'Parse Failed', bg: 'bg-red-500/20', text: 'text-red-400', icon: AlertTriangle },
};

const getFileIcon = (filename) => {
  if (!filename) return TYPE_ICONS.default;
  const ext = filename.split('.').pop()?.toLowerCase();
  return TYPE_ICONS[ext] || TYPE_ICONS.default;
};

// ============================================================================
// STATS BAR COMPONENT
// ============================================================================

function StatsBar({ stats, onFilterClick }) {
  return (
    <div className="grid grid-cols-4 gap-4">
      <button 
        onClick={() => onFilterClick('all')}
        className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-4 hover:border-emerald-500/30 transition-colors text-left"
        data-testid="stat-total"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-emerald-500/20">
            <FolderOpen size={18} className="text-emerald-400" />
          </div>
          <div>
            <div className="text-2xl font-bold text-white">{stats?.total || 0}</div>
            <div className="text-xs text-[#6B6B6B]">Total Evidence</div>
          </div>
        </div>
      </button>

      <button 
        onClick={() => onFilterClick('pending')}
        className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-4 hover:border-amber-500/30 transition-colors text-left"
        data-testid="stat-pending"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-amber-500/20">
            <Clock size={18} className="text-amber-400" />
          </div>
          <div>
            <div className="text-2xl font-bold text-white">{stats?.pending_approval || 0}</div>
            <div className="text-xs text-[#6B6B6B]">Pending Approval</div>
          </div>
        </div>
      </button>

      <button 
        onClick={() => onFilterClick('approved')}
        className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-4 hover:border-emerald-500/30 transition-colors text-left"
        data-testid="stat-approved"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-blue-500/20">
            <CheckCircle2 size={18} className="text-blue-400" />
          </div>
          <div>
            <div className="text-2xl font-bold text-white">{stats?.approved || 0}</div>
            <div className="text-xs text-[#6B6B6B]">Approved</div>
          </div>
        </div>
      </button>

      <button 
        onClick={() => onFilterClick('rejected')}
        className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-4 hover:border-red-500/30 transition-colors text-left"
        data-testid="stat-rejected"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-red-500/20">
            <AlertTriangle size={18} className="text-red-400" />
          </div>
          <div>
            <div className="text-2xl font-bold text-white">{stats?.rejected || 0}</div>
            <div className="text-xs text-[#6B6B6B]">Rejected</div>
          </div>
        </div>
      </button>
    </div>
  );
}

// ============================================================================
// BULK ACTION BAR
// ============================================================================

function BulkActionBar({ selectedCount, onDelete, onReassign, onApprove, onClear }) {
  const { toast } = useToast();

  return (
    <div className="sticky top-0 z-20 bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 flex items-center justify-between">
      <div className="flex items-center gap-4">
        <span className="text-sm font-medium text-blue-300">
          {selectedCount} item{selectedCount > 1 ? 's' : ''} selected
        </span>
        <Button 
          size="sm" 
          variant="ghost" 
          onClick={onClear}
          className="text-gray-400 hover:text-white"
        >
          <X size={14} className="mr-1" />
          Clear
        </Button>
      </div>
      
      <div className="flex items-center gap-2">
        <Button 
          size="sm" 
          variant="outline" 
          onClick={onApprove}
          className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/20"
          data-testid="bulk-approve-btn"
        >
          <CheckCircle2 size={14} className="mr-1.5" />
          Approve
        </Button>
        <Button 
          size="sm" 
          variant="outline" 
          onClick={onReassign}
          className="border-[#2A2A2A] text-gray-300"
          data-testid="bulk-reassign-btn"
        >
          <Users size={14} className="mr-1.5" />
          Reassign
        </Button>
        <Button 
          size="sm" 
          variant="outline" 
          onClick={onDelete}
          className="border-red-500/30 text-red-400 hover:bg-red-500/20"
          data-testid="bulk-delete-btn"
        >
          <Trash2 size={14} className="mr-1.5" />
          Delete
        </Button>
      </div>
    </div>
  );
}

// ============================================================================
// EVIDENCE CARD COMPONENT
// ============================================================================

function EvidenceCard({ item, viewMode, isSelected, onToggleSelect, onOpenDetail, onViewLineage, onFindSimilar }) {
  const fileType = item.original_filename?.split('.').pop()?.toLowerCase() || 'default';
  const TypeIcon = TYPE_ICONS[fileType] || TYPE_ICONS.default;
  const status = item.ownership?.approval_status || item.status || 'uploaded';
  const statusConfig = STATUS_CONFIG[status] || STATUS_CONFIG.uploaded;
  const StatusIcon = statusConfig.icon;
  
  const frameworks = item.framework_mappings?.map(m => m.framework_code) || [];
  const isOrphaned = frameworks.length === 0;

  const handleClick = (e) => {
    // Don't open detail if clicking checkbox area
    if (e.target.closest('[data-checkbox]')) return;
    onOpenDetail(item.id);
  };

  const handleCheckboxClick = (e) => {
    e.stopPropagation();
    onToggleSelect(item.id);
  };

  if (viewMode === 'list') {
    return (
      <div 
        className={`bg-[#111111] border rounded-xl p-4 transition-all flex items-center gap-4 cursor-pointer
          ${isSelected ? 'border-blue-500/50 bg-blue-500/5' : 'border-[#1F1F1F] hover:border-emerald-500/30'}`}
        onClick={handleClick}
        data-testid={`evidence-item-${item.id}`}
      >
        <div data-checkbox onClick={handleCheckboxClick}>
          <Checkbox checked={isSelected} />
        </div>

        <div className="p-3 rounded-lg bg-[#1F1F1F]">
          <TypeIcon size={20} className="text-emerald-400" />
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-sm font-semibold text-white truncate">
              {item.original_filename || item.title || item.name}
            </h3>
            <span className={`px-2 py-0.5 text-[10px] uppercase tracking-wider rounded ${statusConfig.bg} ${statusConfig.text}`}>
              {statusConfig.label}
            </span>
          </div>
          <div className="flex items-center gap-4 text-xs text-[#6B6B6B]">
            <span className="flex items-center gap-1">
              <Calendar size={12} />
              {new Date(item.uploaded_at || item.created_at).toLocaleDateString()}
            </span>
            <span>{item.file_size ? `${Math.round(item.file_size / 1024)} KB` : ''}</span>
            <div className="flex items-center gap-1">
              {frameworks.slice(0, 3).map(fw => (
                <span key={fw} className="px-1.5 py-0.5 bg-[#1F1F1F] rounded text-[#A0A0A0]">{fw}</span>
              ))}
              {frameworks.length > 3 && (
                <span className="text-[#6B6B6B]">+{frameworks.length - 3}</span>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button 
            onClick={(e) => { e.stopPropagation(); onFindSimilar && onFindSimilar(item); }}
            className="p-2 rounded-lg bg-[#1F1F1F] text-[#6B6B6B] hover:bg-purple-500/20 hover:text-purple-400 transition-colors"
            title="Find Similar Evidence"
            data-testid="find-similar-btn"
          >
            <Sparkles size={16} />
          </button>
          <button 
            onClick={(e) => { e.stopPropagation(); onViewLineage && onViewLineage(item.id); }}
            className="p-2 rounded-lg bg-[#1F1F1F] text-[#6B6B6B] hover:bg-teal-500/20 hover:text-teal-400 transition-colors"
            title="View Lineage"
          >
            <Link2 size={16} />
          </button>
          <button 
            onClick={(e) => { e.stopPropagation(); onOpenDetail(item.id); }}
            className="p-2 rounded-lg bg-[#1F1F1F] text-[#6B6B6B] hover:bg-emerald-500/20 hover:text-emerald-400 transition-colors"
          >
            <Eye size={16} />
          </button>
        </div>
      </div>
    );
  }

  // Grid view (default)
  return (
    <div 
      className={`bg-[#111111] border rounded-xl p-5 transition-all group cursor-pointer
        ${isSelected ? 'border-blue-500/50 bg-blue-500/5' : 'border-[#1F1F1F] hover:border-emerald-500/30'}`}
      onClick={handleClick}
      data-testid={`evidence-item-${item.id}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div data-checkbox onClick={handleCheckboxClick}>
            <Checkbox checked={isSelected} />
          </div>
          <div className="p-3 rounded-lg bg-[#1F1F1F] group-hover:bg-emerald-500/20 transition-colors">
            <TypeIcon size={24} className="text-emerald-400" />
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isOrphaned && (
            <span className="px-2 py-1 text-[10px] rounded flex items-center gap-1 bg-amber-500/20 text-amber-400" title="Not mapped to any framework">
              <AlertTriangle size={10} />
            </span>
          )}
          <span className={`px-2 py-1 text-xs rounded flex items-center gap-1 ${statusConfig.bg} ${statusConfig.text}`}>
            <StatusIcon size={12} />
            {statusConfig.label}
          </span>
        </div>
      </div>

      {/* Content */}
      <h3 className="text-sm font-semibold text-white mb-2 line-clamp-2 group-hover:text-emerald-400 transition-colors">
        {item.original_filename || item.title || item.name}
      </h3>
      
      <p className="text-xs text-[#6B6B6B] mb-3">{item.category || item.evidence_type}</p>

      {/* Framework Tags */}
      <div className="flex flex-wrap gap-1 mb-4">
        {frameworks.slice(0, 3).map(fw => (
          <span 
            key={fw} 
            className="px-2 py-0.5 text-[10px] font-medium bg-[#1F1F1F] text-[#A0A0A0] rounded"
          >
            {fw}
          </span>
        ))}
        {frameworks.length > 3 && (
          <span className="px-2 py-0.5 text-[10px] text-[#6B6B6B]">
            +{frameworks.length - 3} more
          </span>
        )}
        {frameworks.length === 0 && (
          <span className="px-2 py-0.5 text-[10px] text-[#4B4B4B] italic">
            No framework mappings
          </span>
        )}
      </div>

      {/* Meta */}
      <div className="flex items-center justify-between text-xs text-[#6B6B6B] pt-3 border-t border-[#1F1F1F]">
        <span className="flex items-center gap-1">
          <Calendar size={12} />
          {new Date(item.uploaded_at || item.created_at).toLocaleDateString()}
        </span>
        <span>{item.file_size ? `${Math.round(item.file_size / 1024)} KB` : ''}</span>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 mt-3 pt-3 border-t border-[#1F1F1F]">
        <button 
          onClick={(e) => { e.stopPropagation(); onOpenDetail(item.id); }}
          className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-[#1F1F1F] hover:bg-emerald-500/20 text-[#A0A0A0] hover:text-emerald-400 rounded-lg transition-colors text-xs"
        >
          <Eye size={14} />
          View
        </button>
        <button 
          onClick={(e) => { e.stopPropagation(); onFindSimilar && onFindSimilar(item); }}
          className="flex items-center justify-center gap-2 px-3 py-2 bg-[#1F1F1F] hover:bg-purple-500/20 text-[#A0A0A0] hover:text-purple-400 rounded-lg transition-colors text-xs"
          title="Find Similar Evidence"
          data-testid="find-similar-btn"
        >
          <Sparkles size={14} />
        </button>
        <button 
          onClick={(e) => { e.stopPropagation(); onViewLineage && onViewLineage(item.id); }}
          className="flex items-center justify-center gap-2 px-3 py-2 bg-[#1F1F1F] hover:bg-teal-500/20 text-[#A0A0A0] hover:text-teal-400 rounded-lg transition-colors text-xs"
          title="View Lineage"
        >
          <Link2 size={14} />
        </button>
        <button 
          className="flex items-center justify-center gap-2 px-3 py-2 bg-[#1F1F1F] hover:bg-blue-500/20 text-[#A0A0A0] hover:text-blue-400 rounded-lg transition-colors text-xs"
        >
          <Download size={14} />
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// MAIN PAGE COMPONENT
// ============================================================================

export default function EvidenceLibraryPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();
  
  // Lineage drawer state
  const lineageDrawer = useLineageDrawer();
  
  // URL params
  const cycleFilter = searchParams.get('cycle');
  const selectedId = searchParams.get('selected');
  
  // State
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterFramework, setFilterFramework] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [sortBy, setSortBy] = useState('-uploaded_at');
  const [viewMode, setViewMode] = useState('grid');
  const [evidence, setEvidence] = useState([]);
  const [listTotal, setListTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Selection state
  const [selectedIds, setSelectedIds] = useState(new Set());
  
  // Similar Evidence Modal state
  const [similarModalOpen, setSimilarModalOpen] = useState(false);
  const [similarEvidenceItem, setSimilarEvidenceItem] = useState(null);

  // Handle Find Similar click
  const handleFindSimilar = useCallback((item) => {
    setSimilarEvidenceItem(item);
    setSimilarModalOpen(true);
  }, []);

  // Fetch evidence from backend
  const fetchEvidence = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      let url = `${API}/v1/evidence/list?limit=50`;
      if (cycleFilter) {
        url += `&disclosure_cycle_id=${cycleFilter}`;
      }
      if (filterStatus !== 'all') {
        // Map filter to approval status
        const statusMap = { pending: 'pending', approved: 'approved', rejected: 'rejected' };
        if (statusMap[filterStatus]) {
          url += `&status=${statusMap[filterStatus]}`;
        }
      }
      if (filterCategory !== 'all') {
        url += `&category=${filterCategory}`;
      }
      if (filterFramework !== 'all') {
        url += `&framework_code=${filterFramework}`;
      }
      if (searchQuery) {
        url += `&search=${encodeURIComponent(searchQuery)}`;
      }
      
      // Fetch evidence list and stats in parallel
      const [listRes, statsRes] = await Promise.all([
        fetch(url, { headers }),
        fetch(`${API}/v1/evidence/statistics`, { headers })
      ]);
      
      if (listRes.ok) {
        const data = await listRes.json();
        // Tolerant of the mounted PG shape ({evidence,total}), the legacy hub
        // shape ({items}), and a bare array — and normalize each row so the
        // cards get a stable id + truthful status regardless (evidenceData.js).
        const rows = extractEvidenceRows(data).map(normalizeEvidence);
        setEvidence(rows);
        setListTotal(extractListTotal(data, rows));
      } else {
        const errData = await listRes.json().catch(() => ({}));
        const rawMsg = errData.detail || errData.error || '';
        console.error('Evidence list fetch failed:', rawMsg);
        const friendly = (!rawMsg || rawMsg.length > 120 || /psycopg2|sqlalchemy|traceback/i.test(rawMsg))
          ? `Evidence service is temporarily unavailable (${listRes.status})`
          : rawMsg;
        throw new Error(friendly);
      }

      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData);
      }
    } catch (err) {
      console.error('Error fetching evidence:', err);
      setError(err.message);
      // A failed load is an ERROR state, not "no evidence". Clear the grid and
      // the count so nothing stale is shown, and let the error banner render
      // instead of the empty state (see the view-state gate below).
      setEvidence([]);
      setListTotal(0);
      setStats({ total: 0, pending_approval: 0, approved: 0, rejected: 0 });
    } finally {
      setLoading(false);
    }
  }, [cycleFilter, filterStatus, filterCategory, filterFramework, searchQuery]);

  useEffect(() => {
    fetchEvidence();
  }, [fetchEvidence]);

  // Filter and sort evidence (client-side)
  const filteredEvidence = evidence
    .filter(item => {
      const filename = item.original_filename || item.title || item.name || '';
      const matchesSearch = filename.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesSearch;
    })
    .sort((a, b) => {
      // Determine sort direction and field
      const isDescending = sortBy.startsWith('-');
      const field = isDescending ? sortBy.slice(1) : sortBy;
      
      let valueA, valueB;
      
      switch (field) {
        case 'uploaded_at':
          valueA = new Date(a.uploaded_at || a.created_at || 0).getTime();
          valueB = new Date(b.uploaded_at || b.created_at || 0).getTime();
          break;
        case 'original_filename':
          valueA = (a.original_filename || a.title || a.name || '').toLowerCase();
          valueB = (b.original_filename || b.title || b.name || '').toLowerCase();
          break;
        case 'file_size':
          valueA = a.file_size || 0;
          valueB = b.file_size || 0;
          break;
        default:
          valueA = new Date(a.uploaded_at || a.created_at || 0).getTime();
          valueB = new Date(b.uploaded_at || b.created_at || 0).getTime();
      }
      
      // Compare values
      if (typeof valueA === 'string' && typeof valueB === 'string') {
        return isDescending ? valueB.localeCompare(valueA) : valueA.localeCompare(valueB);
      }
      
      return isDescending ? valueB - valueA : valueA - valueB;
    });

  // View data-state — mutually distinguishable so an active filter or a
  // failed fetch is never rendered as "no evidence" (docs/TRUST_STATE_MODEL).
  const filtersActive = hasActiveFilter({ searchQuery, filterStatus, filterFramework, filterCategory });
  const viewState = evidenceViewState({
    loading, error, shownCount: filteredEvidence.length, filtersActive,
  });

  // Get unique frameworks for filter
  const allFrameworks = [...new Set(
    evidence.flatMap(item => 
      item.framework_mappings?.map(m => m.framework_code) || []
    )
  )];

  // Get unique categories
  const allCategories = [...new Set(
    evidence.map(item => item.category).filter(Boolean)
  )];

  // Selection handlers
  const toggleSelection = (id) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredEvidence.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredEvidence.map(e => e.id)));
    }
  };

  const clearSelection = () => setSelectedIds(new Set());

  // Bulk action handlers
  const handleBulkDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete ${selectedIds.size} items?`)) return;
    
    try {
      const data = await apiFetch(`/v1/evidence/bulk-action`, {
        method: 'POST',
        body: JSON.stringify({
          action: 'delete',
          evidence_ids: Array.from(selectedIds),
          params: {}
        })
      });

      if (data && !data._error) {
        toast({ title: 'Deleted', description: `${data.count} items deleted` });
        clearSelection();
        fetchEvidence();
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete items', variant: 'destructive' });
    }
  };

  const handleBulkApprove = async () => {
    try {
      const data = await apiFetch(`/v1/evidence/bulk-action`, {
        method: 'POST',
        body: JSON.stringify({
          action: 'approve',
          evidence_ids: Array.from(selectedIds),
          params: {}
        })
      });

      if (data && !data._error) {
        toast({ title: 'Approved', description: `${data.count} items approved` });
        clearSelection();
        fetchEvidence();
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to approve items', variant: 'destructive' });
    }
  };

  const handleBulkReassign = async () => {
    const newOwner = window.prompt('Enter new owner email:');
    if (!newOwner) return;
    
    try {
      const data = await apiFetch(`/v1/evidence/bulk-action`, {
        method: 'POST',
        body: JSON.stringify({
          action: 'reassign',
          evidence_ids: Array.from(selectedIds),
          params: { new_owner_id: newOwner, new_owner_email: newOwner }
        })
      });

      if (data && !data._error) {
        toast({ title: 'Reassigned', description: `${data.count} items reassigned` });
        clearSelection();
        fetchEvidence();
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to reassign items', variant: 'destructive' });
    }
  };

  // Detail drawer handlers
  const openDetail = (id) => {
    setSearchParams(prev => {
      prev.set('selected', id);
      return prev;
    });
  };

  const closeDetail = () => {
    setSearchParams(prev => {
      prev.delete('selected');
      return prev;
    });
  };

  // Stats filter click handler
  const handleStatsFilterClick = (status) => {
    if (status === 'all') {
      setFilterStatus('all');
    } else {
      setFilterStatus(status);
    }
  };

  const clearFilters = () => {
    setSearchQuery('');
    setFilterStatus('all');
    setFilterFramework('all');
    setFilterCategory('all');
  };

  return (
    <div className="space-y-6" data-testid="evidence-library-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Evidence Hub</h1>
          <p className="text-sm text-[#6B6B6B] mt-1">
            {cycleFilter ? 'Evidence for selected cycle' : 'Manage and organize your compliance evidence'}
          </p>
        </div>
        <Link
          to="/evidence/upload"
          className="flex items-center gap-2 px-4 py-2 bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/30 text-emerald-400 rounded-lg transition-colors"
          data-testid="upload-evidence-btn"
        >
          <Upload size={16} />
          <span className="text-sm font-medium">Upload Evidence</span>
        </Link>
      </div>

      {/* Stats Bar */}
      <StatsBar stats={stats} onFilterClick={handleStatsFilterClick} />

      {/* Filters & View Toggle */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B6B6B]" />
          <input
            type="text"
            placeholder="Search evidence by name, description, or tags..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-[#111111] border border-[#1F1F1F] rounded-lg text-sm text-white placeholder:text-[#6B6B6B] focus:border-emerald-500/50 focus:outline-none"
            data-testid="evidence-search-input"
          />
        </div>

        <div className="flex items-center gap-2">
          <Filter size={14} className="text-[#6B6B6B]" />
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-2 bg-[#111111] border border-[#1F1F1F] rounded-lg text-sm text-white focus:border-emerald-500/50 focus:outline-none"
          >
            <option value="all">All Status</option>
            <option value="pending">Pending Approval</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>

          <select
            value={filterFramework}
            onChange={(e) => setFilterFramework(e.target.value)}
            className="px-3 py-2 bg-[#111111] border border-[#1F1F1F] rounded-lg text-sm text-white focus:border-emerald-500/50 focus:outline-none"
          >
            <option value="all">All Frameworks</option>
            {allFrameworks.map(fw => (
              <option key={fw} value={fw}>{fw}</option>
            ))}
          </select>

          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="px-3 py-2 bg-[#111111] border border-[#1F1F1F] rounded-lg text-sm text-white focus:border-emerald-500/50 focus:outline-none"
          >
            <option value="all">All Categories</option>
            {allCategories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>

          {(searchQuery || filterStatus !== 'all' || filterFramework !== 'all' || filterCategory !== 'all') && (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={clearFilters}
              className="border-[#2A2A2A] text-gray-400"
            >
              Clear
            </Button>
          )}
        </div>

        <div className="flex items-center gap-1 p-1 bg-[#111111] border border-[#1F1F1F] rounded-lg">
          <button
            onClick={() => setViewMode('grid')}
            className={`p-2 rounded ${viewMode === 'grid' ? 'bg-emerald-500/20 text-emerald-400' : 'text-[#6B6B6B] hover:text-white'}`}
          >
            <Grid size={16} />
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`p-2 rounded ${viewMode === 'list' ? 'bg-emerald-500/20 text-emerald-400' : 'text-[#6B6B6B] hover:text-white'}`}
          >
            <List size={16} />
          </button>
        </div>
      </div>

      {/* View Controls Row */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          {/* Select All */}
          <Button
            variant="outline"
            size="sm"
            onClick={toggleSelectAll}
            className="border-[#2A2A2A] text-gray-400"
          >
            {selectedIds.size === filteredEvidence.length && filteredEvidence.length > 0 ? (
              <CheckSquare className="h-4 w-4 mr-2 text-blue-400" />
            ) : (
              <Square className="h-4 w-4 mr-2" />
            )}
            Select All
          </Button>

          {/* Sort */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">Sort:</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-3 py-1.5 bg-[#111111] border border-[#1F1F1F] rounded-lg text-sm text-white focus:outline-none"
            >
              <option value="-uploaded_at">Most Recent</option>
              <option value="uploaded_at">Oldest First</option>
              <option value="original_filename">Name (A-Z)</option>
              <option value="-file_size">Largest First</option>
            </select>
          </div>
        </div>

        {/* Results count — denominator is the list endpoint's OWN total, so
            the count and the grid can never come from different datasets. */}
        <div className="text-sm text-[#6B6B6B]" data-testid="results-count">
          Showing {filteredEvidence.length} of {listTotal} items
          {filtersActive && <span className="text-[#4B4B4B]"> (filtered)</span>}
        </div>
      </div>

      {/* Bulk Action Bar */}
      {selectedIds.size > 0 && (
        <BulkActionBar
          selectedCount={selectedIds.size}
          onDelete={handleBulkDelete}
          onReassign={handleBulkReassign}
          onApprove={handleBulkApprove}
          onClear={clearSelection}
        />
      )}

      {/* Cross-Framework Map Link */}
      <Link 
        to="/evidence/cross-framework"
        className="block bg-[#111111] border border-[#1F1F1F] rounded-xl p-4 hover:border-teal-500/30 transition-colors"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-teal-500/20">
              <Network size={18} className="text-teal-400" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-white">Cross-Framework Map</h3>
              <p className="text-xs text-[#6B6B6B]">See how evidence maps across multiple frameworks (Superpower #1)</p>
            </div>
          </div>
          <ExternalLink size={16} className="text-[#6B6B6B]" />
        </div>
      </Link>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <RefreshCw size={24} className="text-emerald-400 animate-spin" />
          <span className="ml-3 text-[#A0A0A0]">Loading evidence...</span>
        </div>
      )}

      {/* Error State — the load failed, so we show no data and no misleading
          count. Offer a retry; never fall through to the empty state. */}
      {viewState === 'error' && (
        <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-xl flex items-center justify-between" data-testid="evidence-error">
          <div className="flex items-center gap-2 text-red-400 text-sm">
            <AlertTriangle size={16} />
            <span>Couldn't load evidence: {error}</span>
          </div>
          <Button variant="outline" size="sm" onClick={fetchEvidence} className="border-red-500/30 text-red-400 hover:bg-red-500/20" data-testid="evidence-retry-btn">
            <RefreshCw size={14} className="mr-1.5" />
            Retry
          </Button>
        </div>
      )}

      {/* Evidence Grid/List — only when there is something to show */}
      {viewState === 'ready' && (
        <div className={viewMode === 'grid'
          ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4'
          : 'space-y-3'
        }>
          {filteredEvidence.map(item => (
            <EvidenceCard
              key={item.id}
              item={item}
              viewMode={viewMode}
              isSelected={selectedIds.has(item.id)}
              onToggleSelect={toggleSelection}
              onOpenDetail={openDetail}
              onViewLineage={(evidenceId) => lineageDrawer.openDrawer({ evidenceId, sourceContext: 'evidence' })}
              onFindSimilar={handleFindSimilar}
            />
          ))}
        </div>
      )}

      {/* Filtered-empty — records exist, the current filters hide them.
          Distinct from the true empty state so we never tell the user to
          "upload your first document" when evidence is merely filtered out. */}
      {viewState === 'filtered_empty' && (
        <div className="text-center py-12 bg-[#111111] border border-[#1F1F1F] rounded-xl" data-testid="evidence-filtered-empty">
          <Filter size={48} className="mx-auto text-[#2F2F2F] mb-4" />
          <h3 className="text-lg font-medium text-white mb-2">No evidence matches these filters</h3>
          <p className="text-sm text-[#6B6B6B] mb-4">
            {listTotal > 0
              ? `${listTotal} item${listTotal === 1 ? '' : 's'} in this hub — none match the active search or filters.`
              : 'Try adjusting your search or filter criteria.'}
          </p>
          <Button variant="outline" size="sm" onClick={clearFilters} className="border-[#2A2A2A] text-gray-300" data-testid="clear-filters-btn">
            Clear filters
          </Button>
        </div>
      )}

      {/* True empty — the hub really has no evidence. */}
      {viewState === 'empty' && (
        <div className="text-center py-12 bg-[#111111] border border-[#1F1F1F] rounded-xl" data-testid="evidence-empty">
          <FolderOpen size={48} className="mx-auto text-[#2F2F2F] mb-4" />
          <h3 className="text-lg font-medium text-white mb-2">No evidence yet</h3>
          <p className="text-sm text-[#6B6B6B] mb-4">Upload your first evidence document</p>
          <Link
            to="/evidence/upload"
            className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/30 text-emerald-400 rounded-lg transition-colors"
          >
            <Upload size={16} />
            Upload Evidence
          </Link>
        </div>
      )}

      {/* Evidence Detail Drawer */}
      <EvidenceDetailDrawer
        evidenceId={selectedId}
        open={!!selectedId}
        onClose={closeDetail}
        onUpdate={fetchEvidence}
      />

      {/* Evidence Lineage Drawer */}
      <LineageDrawer
        isOpen={lineageDrawer.isOpen}
        onClose={lineageDrawer.closeDrawer}
        evidenceId={lineageDrawer.evidenceId}
        claimId={lineageDrawer.claimId}
        sourceContext={lineageDrawer.sourceContext}
      />

      {/* Similar Evidence Modal */}
      <SimilarEvidenceModal
        isOpen={similarModalOpen}
        onClose={() => setSimilarModalOpen(false)}
        evidence={similarEvidenceItem}
        onSelectEvidence={(docId) => {
          setSimilarModalOpen(false);
          openDetail(docId);
        }}
      />

      {/* Live User Journeys */}
      <JourneySection title="Live Journeys">
        <JourneyPanel title="Cross-Framework Semantic Map" ucId="UC-09"><CrossFrameworkMap /></JourneyPanel>
        <JourneyPanel title="Emission Factor Search" ucId="UC-10"><EFSearchPanel /></JourneyPanel>
        <JourneyPanel title="PII Scanner & Redaction" ucId="UC-27"><PIIScanner /></JourneyPanel>
        <JourneyPanel title="Data Connector Configuration" ucId="UC-05"><ConnectorConfig /></JourneyPanel>
      </JourneySection>
    </div>
  );
}
